import React, { useRef, useEffect } from "react"
import ReactHtmlParser from "react-html-parser"
import Svg from "../_svgs/svg"
import YoutubeEmbed from "../youtube-embed/YouTubeEmbed"
import { generateRandomString } from "../../helpers/keys"

function PracticalCardItem({
  portraitPhoto,
  activateSplash,
  splashLabel,
  title,
  readMore,
  readLess, // Outer Card
  header,
  introText,
  introImage,
  explainerText,
  introImageUrl,
  introImageUrlLabel,
  outroText,
  additionalImageSections, //Inner Card Content
  activeCard,
  setActiveCard,
  setAreCardsClosed,
  elementId,
  maximumCardTitleHeightValue,
  setMaximumCardTitleHeightValue, //Functional Card Variables
  windowWidth,
  innerVideo,
  youtubeID,
  thumbnailImage,
  ariaDescription,
}) {
  var scrollRef = useRef()
  var titleHeightRef = useRef()

  const handleResize = () => {
    if (titleHeightRef.current) {
      const element = titleHeightRef.current
      const height = element.offsetHeight

      if (windowWidth < 750) {
        titleHeightRef.current.style.height = ""
        return
      }

      if (maximumCardTitleHeightValue <= height) {
        setMaximumCardTitleHeightValue(height)
      }

      //hard capping the height on the card at 180px.
      if (height < maximumCardTitleHeightValue) {
        element.style.height = `${maximumCardTitleHeightValue > 180 ? 180 : maximumCardTitleHeightValue}px`
      }
    }
    return
  }

  useEffect(() => {
    if (titleHeightRef.current) {
      const element = titleHeightRef.current
      const height = element.offsetHeight

      if (windowWidth < 768) {
        titleHeightRef.current.style.height = ""
        return
      }

      if (maximumCardTitleHeightValue < height) {
        setMaximumCardTitleHeightValue(height)
      }

      if (height < maximumCardTitleHeightValue) {
        element.style.height = `${maximumCardTitleHeightValue > 180 ? 180 : maximumCardTitleHeightValue}px`
      }
    }
  })

  useEffect(() => {
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [windowWidth])

  //Rich Text Formatting
  let formattedIntroText = ReactHtmlParser(introText)
  let formattedOutroText = ReactHtmlParser(outroText)

  /**
   * Toggles state switches in the parent component that controls what
   * styles are put on the card elements
   * @setActiveCard Null by default, when a card is toggled, the activeCard variable that setActiveCard sets, is then set to the elementId of the card.
   * @setAreCardsClosed true by default. When a card is toggled on, this is set to false, meaning every other card that is not active, is set to inactive in the styling.
   * @scrollToTopOfElement is a method run when a card is closed and no new card is opened. it scrolls to the top of the closed element.
   */
  const ToggleCard = () => {
    if (activeCard === elementId) {
      setActiveCard(null)
      setAreCardsClosed(true)
      scrollIntoView()
    } else {
      setActiveCard(elementId)
      setAreCardsClosed(false)
    }
  }

  const scrollIntoView = () => {
    let element = scrollRef.current

    const siteHeader = document.getElementsByClassName("site-header")[0]
    const headerOffset = siteHeader.clientHeight + 20
    const elementPosition = element.getBoundingClientRect().top
    const offsetPosition = elementPosition + window.pageYOffset - headerOffset

    window.scrollTo({ top: offsetPosition, behavior: "smooth" })
  }

  const htmlAdditionalImageSections = additionalImageSections
    ? additionalImageSections.map((element) => {
        return (
          <div className="imagecontainer" key={generateRandomString()}>
            {element.youtubeID ? (
              <YoutubeEmbed
                embedId={element.youtubeID}
                classDecleration="practical-card-youtube"
                backgroundImage={!element.thumbnailImage ? undefined : element.thumbnailImage}
                ariaVideoDescription={element.ariaDescription}
              />
            ) : element.innerVideo ? (
              <video
                src={element.innerVideo}
                controls={true}
                playsInline
                poster={thumbnailImage}
                preload={"auto"}
                loading="eager"
                fetchPriority="high"
              />
            ) : element.additionalIntroImage ? (
              <img src={element.additionalIntroImage} />
            ) : null}

            <div className="imgtext">
              {element.additionalIntroImageExplainerText ? <p>{element.additionalIntroImageExplainerText}</p> : null}
              {!!element.additionalIntroImageUrl || !!element.additionalIntroImageUrl ? (
                <a href={element.additionalIntroImageUrl}>
                  <Svg iconName="arrowright-slim" />
                  {element.additionalIntroImageUrlLabel}
                </a>
              ) : null}
            </div>
          </div>
        )
      })
    : undefined

  return (
    <div className="cardcontainer" id={elementId} ref={scrollRef}>
      <div className={`card ${activeCard === elementId ? "active" : "inactive"}`} onClick={ToggleCard}>
        {activateSplash ? <div className="splash">{splashLabel}</div> : null}
        <img src={portraitPhoto} />
        <div className="contentarea">
          <h3 ref={titleHeightRef}>{title}</h3>
          <div className="readmore">{activeCard === elementId ? readLess || "Læs Mindre" : readMore || "Læs Mere"}</div>
        </div>
      </div>
      <div className="cardinfo">
        <h3 className={"desktop-only"}>{header}</h3>
        <div className="rich-text">{formattedIntroText}</div>

        {introImage || innerVideo || youtubeID ? (
          <div className="imagecontainer">
            {youtubeID ? (
              <YoutubeEmbed
                embedId={youtubeID}
                classDecleration="practical-card-youtube"
                backgroundImage={!thumbnailImage ? undefined : thumbnailImage}
                ariaVideoDescription={ariaDescription}
              />
            ) : innerVideo ? (
              <video src={innerVideo} controls={true} playsInline poster={thumbnailImage} preload={"auto"} loading="eager" fetchPriority="high" />
            ) : introImage ? (
              <img src={introImage} />
            ) : null}
            <div className="imgtext">
              {explainerText ? <p>{explainerText}</p> : null}
              {!!introImageUrl || !!introImageUrlLabel ? (
                <a href={introImageUrl}>
                  <Svg iconName="arrowright-slim" />
                  {introImageUrlLabel}
                </a>
              ) : null}
            </div>
          </div>
        ) : null}

        {htmlAdditionalImageSections}

        {formattedOutroText ? <div className="rich-text">{formattedOutroText}</div> : null}
        <div className="readless" onClick={ToggleCard}>
          {readLess || "Læs Mindre"}
        </div>
      </div>
    </div>
  )
}

export default PracticalCardItem
